import { TouchableOpacity, View } from "react-native"
import SummarySection from "./SummarySection"
import FinancingCostsSection from "./FinancingCostsSection"
import AdditionalCostsSection from "./AdditionalCostsSection"
import DealMeterBlock from "@/components/analysis/calculate/DealMeterBlock"
import OperatingAssumptionsSection from "@/components/analysis/calculate/OperatingAssumptionsSection"
import React, { useCallback, useRef } from "react"
import { useAnalysis } from "@/queries/analysis"
import { type AnalysisType } from "@/generated/graphql"
import {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetModal,
} from "@gorhom/bottom-sheet"
import { BFColors } from "@/constants/Colors"
import { PreApprovalPopover } from "@/components/widgets/PreApprovalWidget"
import { useStore } from "@nanostores/react"
import { $preApprovalHasUpdated } from "@/stores/preapproval"
import { useCreateLoan } from "@/hooks/useCreateLoan"
import BFText from "@/components/standard/BFText"
import { preApprovalView, requestPreApproval } from "@/analytics/events/home"
import { useScreenName } from "@/hooks/useScreenName"

interface CalculateTabContentProps {
  analysisId: string
  onEnterArv: () => void
}

const CalculateTabContent: React.FC<CalculateTabContentProps> = ({
  analysisId,
  onEnterArv,
}) => {
  const { data: analysisQuery } = useAnalysis(analysisId)
  const { createLoan, isPending } = useCreateLoan(analysisId)
  const currentScreen = useScreenName()

  const onPreApprovalPress = () => {
    const preapprovalState = $preApprovalHasUpdated.get()
    if (!preapprovalState) {
      requestPreApproval()
    }

    preApprovalView(currentScreen)
    bottomSheetModalRef.current?.present()
  }

  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const preApprovalState = useStore($preApprovalHasUpdated)
  const snapPoints = preApprovalState ? ["70%"] : ["88%"]
  // To test.... TODO: Eventually persist value to either display widget or not
  // const hasRequestedLetter: boolean = false

  const analysis = analysisQuery?.analysis as AnalysisType
  if (!analysis) {
    return null
  }

  const renderPreApprovalBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
      />
    ),
    []
  )

  return (
    <View className="flex-1 bg-off-white">
      <DealMeterBlock
        activeTab="Calculate"
        analysis={analysis}
        onEnterArv={onEnterArv}
      />
      <View className="p-6">
        <SummarySection analysis={analysis} />
        <OperatingAssumptionsSection analysis={analysis} />
        <View className="flex-1 items-center mb-1">
          <TouchableOpacity
            testID={"pre-approval-button"}
            onPress={onPreApprovalPress}
            disabled={isPending}
          >
            <BFText className="text-calm-blue text-[14px] font-normal font-figtree-bold underline leading-5 pb-6 tracking-tight">
              Get a Pre-approval letter
            </BFText>
          </TouchableOpacity>
        </View>
        <FinancingCostsSection analysis={analysis} />
        <AdditionalCostsSection analysis={analysis} />

        <BottomSheetModal
          ref={bottomSheetModalRef}
          snapPoints={snapPoints}
          backdropComponent={renderPreApprovalBackdrop}
          index={0}
          backgroundStyle={{ backgroundColor: BFColors.calmBlue }}
          handleIndicatorStyle={{ backgroundColor: "#FFFFFF" }}
          enableDynamicSizing={false}
        >
          <PreApprovalPopover
            fromScreen={"Subject Property"}
            onStart={() => {
              bottomSheetModalRef?.current?.dismiss()
              createLoan()
            }}
            onDismiss={() => {
              bottomSheetModalRef?.current?.dismiss()
            }}
            propertyId={analysis.property.id}
            addressLine1={analysis.property.street}
            addressLine2={`${analysis.property.city}, ${analysis.property.state} ${analysis.property.zipcode}`}
          />
        </BottomSheetModal>
      </View>
    </View>
  )
}

export default CalculateTabContent
